import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Compass, ArrowRight, BookmarkCheck, FileText, Calendar, AlertCircle, RotateCw } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import NameCapture from "@/components/NameCapture";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 20;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function MyAdaptience() {
  const navigate = useNavigate();
  const { account, loading } = useAuth();
  const [justOnboarded, setJustOnboarded] = useState(false);
  const [history, setHistory] = useState(null); // null = loading, [] = empty
  const [historyError, setHistoryError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!loading && !account) navigate("/signin", { replace: true });
  }, [loading, account, navigate]);

  const loadHistory = useCallback(async (offset = 0) => {
    if (offset > 0) setLoadingMore(true);
    try {
      const r = await fetch(`${API}/account/practice-history?limit=${PAGE}&offset=${offset}`, { credentials: "include" });
      if (!r.ok) throw new Error("failed");
      const d = await r.json();
      setHistory((prev) => (offset > 0 && prev ? [...prev, ...(d.items || [])] : d.items || []));
      setHasMore(Boolean(d.has_more));
      setHistoryError(false);
    } catch {
      if (offset === 0) { setHistoryError(true); setHistory(null); }
    }
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    if (account && account.name) loadHistory(0);
  }, [account, loadHistory]);

  if (loading || !account) {
    return (
      <div className="min-h-[100dvh] w-full bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center py-40 text-white/60" data-testid="my-adaptience-loading">
          <Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} />
        </div>
      </div>
    );
  }

  const firstName = (account.name || "").split(" ")[0];
  const justClaimed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("claimed") === "1";

  return (
    <div className="relative min-h-[100dvh] w-full bg-[#0a0a0a]">
      <Navbar />
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -right-24 top-10 h-[46vh] w-[46vh] rounded-full blur-[130px]" style={{ background: "rgba(79,163,217,0.08)" }} />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-3xl px-6 py-16 sm:py-20" data-testid="my-adaptience-page">
        {justClaimed && (
          <div className="mb-8 flex items-center gap-3 rounded-2xl border border-[#4FA3D9]/30 bg-[#4FA3D9]/[0.08] px-5 py-3.5" data-testid="claim-saved-banner">
            <BookmarkCheck size={18} style={{ color: ACCENT }} />
            <p className="text-sm text-white/85">Saved to My Adaptience — this practice is now part of your journey.</p>
          </div>
        )}
        {!account.name ? (
          <NameCapture onDone={() => setJustOnboarded(true)} />
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">My Adaptience</span>
            <h1 className="mb-2 mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {justOnboarded ? "Welcome to Adaptience" : "Welcome back"}{firstName ? `, ${firstName}` : ""}.
            </h1>
            <p className="mb-10 text-sm leading-relaxed text-white/50">Your personal Adaptience space.</p>

            <Link
              to="/simulations"
              data-testid="continue-practising-card"
              className="group block rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-sm transition-colors hover:bg-white/[0.06]"
            >
              <Compass size={20} style={{ color: ACCENT }} />
              <h3 className="mt-4 font-display text-lg font-medium text-white">Continue Practising</h3>
              <p className="mt-1.5 text-sm text-white/55">Explore the simulation library and practise a situation.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm" style={{ color: ACCENT }}>
                Browse simulations <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>

            {/* PHASE 5C — Practice History (account-owned, newest first) */}
            <div className="mt-10" data-testid="practice-history-section">
              <h2 className="mb-5 text-lg font-medium text-white/90">Practice History</h2>

              {historyError ? (
                <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 text-center backdrop-blur-sm" data-testid="practice-history-error">
                  <AlertCircle size={22} className="mx-auto text-white/40" />
                  <p className="mt-4 text-base text-white/80">We couldn't load your practice history.</p>
                  <p className="mt-1 text-sm text-white/45">Your practices are safe — this is just a loading hiccup.</p>
                  <button
                    onClick={() => { setHistory(null); loadHistory(0); }}
                    data-testid="practice-history-retry"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/85 transition-colors hover:bg-white/5"
                  >
                    <RotateCw size={15} /> Try again
                  </button>
                </div>
              ) : history === null ? (
                <div className="flex items-center gap-2 py-8 text-white/50" data-testid="practice-history-loading">
                  <Loader2 size={18} className="animate-spin" style={{ color: ACCENT }} /> Loading your practices…
                </div>
              ) : history.length === 0 ? (
                <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 text-center backdrop-blur-sm" data-testid="practice-history-empty">
                  <FileText size={22} className="mx-auto text-white/35" />
                  <p className="mt-4 font-display text-lg text-white/85">No practices saved yet</p>
                  <p className="mx-auto mt-1.5 max-w-sm text-sm text-white/45">
                    Complete a simulation and your Practice Report will appear here.
                  </p>
                  <Link
                    to="/simulations"
                    data-testid="empty-explore-btn"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ background: ACCENT }}
                  >
                    <Compass size={16} /> Explore Simulations
                  </Link>
                </div>
              ) : (
                <div className="space-y-3" data-testid="practice-history-list">
                  {history.map((h) => (
                    <div
                      key={h.session_id}
                      data-testid="practice-history-item"
                      className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-display text-base text-white" data-testid="history-item-title">{h.title}</p>
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-white/45">
                          <Calendar size={13} /> {fmtDate(h.completed_at)}
                        </p>
                      </div>
                      {h.has_report ? (
                        <button
                          onClick={() => navigate(`/my-adaptience/report/${h.report_id}`)}
                          data-testid="history-view-report-btn"
                          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#4FA3D9]/40 px-5 py-2.5 text-sm font-medium text-[#4FA3D9] transition-colors hover:bg-[#4FA3D9]/10"
                        >
                          <FileText size={15} /> View Practice Report
                        </button>
                      ) : (
                        <span className="shrink-0 text-xs text-white/35" data-testid="history-no-report">Report unavailable</span>
                      )}
                    </div>
                  ))}
                  {hasMore && (
                    <button
                      onClick={() => loadHistory(history.length)}
                      disabled={loadingMore}
                      data-testid="practice-history-load-more"
                      className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 px-5 py-3 text-sm text-white/70 transition-colors hover:bg-white/5 disabled:opacity-60"
                    >
                      {loadingMore ? <Loader2 size={15} className="animate-spin" /> : null} Load more
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
